import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import type { AthleteThreshold } from '../types/database.tables'
import type { CalculatedZone } from '../types/zones'

interface ThresholdForDate {
  vt1Bpm: number | null
  vt2Bpm: number | null
  maxHrBpm: number | null
  hrZones?: { zones: CalculatedZone[]; mode: 'manual' | 'derived' }
  paceZones?: { zones: CalculatedZone[]; mode: 'manual' | 'derived' }
  powerZones?: { zones: CalculatedZone[]; mode: 'manual' | 'derived' }
  effectiveDate: string | null
  source: string | null
  activityType: string | null
}

interface UseAthleteThresholdsReturn {
  thresholds: AthleteThreshold[]
  currentThresholds: ThresholdForDate | null
  loading: boolean
  error: string | null
  getThresholdsForDate: (activityType: string, date: string) => Promise<ThresholdForDate | null>
  addThreshold: (threshold: Omit<AthleteThreshold, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateThreshold: (id: string, threshold: Partial<AthleteThreshold>) => Promise<void>
  deleteThreshold: (id: string) => Promise<void>
  refreshThresholds: () => Promise<void>
}

export const useAthleteThresholds = (athleteId: string): UseAthleteThresholdsReturn => {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ['athlete-thresholds', athleteId], [athleteId])

  const thresholdsQuery = useQuery<AthleteThreshold[]>({
    queryKey,
    enabled: Boolean(athleteId),
    queryFn: async () => {
      console.log('useAthleteThresholds', athleteId)
      const { data, error: supabaseError } = await supabase
        .from('athlete_thresholds')
        .select('*')
        .eq('athlete_id', athleteId)
        .order('effective_date', { ascending: false })
      if (supabaseError) {
        throw new Error(supabaseError.message)
      }
      return data || []
    },
  })

  const currentThresholds = useMemo(() => {
    const data = thresholdsQuery.data
    if (!data || data.length === 0) return null
    const latest = data[0]
    return {
      vt1Bpm: latest.vt1_bpm,
      vt2Bpm: latest.vt2_bpm,
      maxHrBpm: latest.max_hr_bpm,
      effectiveDate: latest.effective_date,
      source: latest.source,
      activityType: latest.activity_type,
    } as ThresholdForDate
  }, [thresholdsQuery.data])

  const getThresholdsForDate = useCallback(
    async (activityType: string, date: string): Promise<ThresholdForDate | null> => {
      if (!athleteId) return null

      try {
        return await queryClient.fetchQuery({
          queryKey: ['athlete-thresholds-for-date', athleteId, activityType, date],
          queryFn: async () => {
            const { data, error: supabaseError } = await supabase.rpc('get_athlete_thresholds_for_date', {
              p_athlete_id: athleteId,
              p_activity_type: activityType,
              p_date: date,
            })

            if (supabaseError) {
              throw new Error(supabaseError.message)
            }

            if (!data || data.length === 0) {
              return null
            }

            const result = data[0]

            let hrZones: CalculatedZone[] | undefined
            let paceZones: CalculatedZone[] | undefined
            let powerZones: CalculatedZone[] | undefined

            if (result.hr_zone_config) {
              hrZones = result.hr_zone_config as unknown as CalculatedZone[]
            }

            if (result.pace_zone_config) {
              paceZones = result.pace_zone_config as unknown as CalculatedZone[]
            }

            if (result.power_zone_config) {
              powerZones = result.power_zone_config as unknown as CalculatedZone[]
            }

            return {
              vt1Bpm: result.vt1_bpm,
              vt2Bpm: result.vt2_bpm,
              maxHrBpm: result.max_hr_bpm,
              hrZones,
              paceZones,
              powerZones,
              effectiveDate: result.effective_date,
              source: result.source,
              activityType: result.activity_type,
            } as ThresholdForDate
          },
        })
      } catch (err) {
        console.error('Error getting thresholds for date:', err)
        return null
      }
    },
    [athleteId, queryClient]
  )

  const addThreshold = useCallback(
    async (thresholdData: Omit<AthleteThreshold, 'id' | 'created_at' | 'updated_at'>) => {
      const { error: supabaseError } = await supabase.from('athlete_thresholds').insert(thresholdData)

      if (supabaseError) {
        throw new Error(supabaseError.message)
      }

      queryClient.invalidateQueries({ queryKey })
    },
    [queryClient, queryKey]
  )

  const updateThreshold = useCallback(
    async (id: string, thresholdData: Partial<AthleteThreshold>) => {
      const { error: supabaseError } = await supabase.from('athlete_thresholds').update(thresholdData).eq('id', id)
      if (supabaseError) {
        throw new Error(supabaseError.message)
      }
      queryClient.invalidateQueries({ queryKey })
    },
    [queryClient, queryKey]
  )

  const deleteThreshold = useCallback(
    async (id: string) => {
      const { error: supabaseError } = await supabase.from('athlete_thresholds').delete().eq('id', id)

      if (supabaseError) {
        throw new Error(supabaseError.message)
      }

      queryClient.invalidateQueries({ queryKey })
    },
    [queryClient, queryKey]
  )

  const refreshThresholds = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey })
  }, [queryClient, queryKey])

  return {
    thresholds: thresholdsQuery.data ?? [],
    currentThresholds,
    loading: thresholdsQuery.isLoading,
    error: thresholdsQuery.error ? (thresholdsQuery.error as Error).message : null,
    getThresholdsForDate,
    addThreshold,
    updateThreshold,
    deleteThreshold,
    refreshThresholds,
  }
}
